import {
  ActivatedRoute, PRIMARY_OUTLET, Router, UrlSegmentGroup, UrlTree,
} from '@angular/router';
import { HideSplash } from 'src/app/@shared/hide-splash.decorator';
import { HttpErrorResponse } from '@angular/common/http';
import { InAppBrowserService } from 'src/app/@core/in-app-browser.service';
import { Injectable, OnDestroy, OnInit, inject } from '@angular/core';
import { ModalController, ModalOptions, NavController, Platform } from '@ionic/angular';
import { NavigationOptions } from '@ionic/angular/common/providers/nav-controller';
import { PageTrack } from 'src/app/@shared/page-track.decorator';
import { PwaService } from 'src/app/@core/pwa.service';
import { SetStatusBar } from 'src/app/@shared/status-bar.decorator';
import { Subject } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { ViewStates } from 'src/app/@lib/view-states';

export const DEBUG_COLORS = {
  DEFAULT: '#00BFFF',
  ERROR: '#FF4500',
  SECONDARY: '#D3D3D3',
  WARN: '#FFA500',
};

@PageTrack()
@HideSplash()
@SetStatusBar()
@Injectable()
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export abstract class BasePage<T = void> implements OnInit, OnDestroy {
  public viewStates: { [key: string]: ViewStates } = {};

  // defaults to page name on base page / simple static page
  // defaults to item.id on detail page
  // defaults to this.listIds[0] on list page
  public currentContentId: string;

  public currentTabUrl: string;
  public backButtonText: string = '';
  public uiPlatform = inject(Platform);
  public isRootPage: boolean;

  protected pageIsVisible: boolean;
  protected isStaticPage: boolean;
  protected refresherElement: HTMLIonRefresherElement;
  protected ngUnsubscribe: Subject<void>;

  // >= angular v14 DI approach
  protected activatedRoute = inject(ActivatedRoute);
  protected inAppBrowserService = inject(InAppBrowserService);
  protected navController = inject(NavController);
  protected modalController = inject(ModalController);
  protected pwaService = inject(PwaService);
  protected router = inject(Router);
  protected translate = inject(TranslateService);

  public abstract readonly pageTrackName: string;

  public ngOnInit(): void {
    this.currentContentId = this.constructor.name;
    this.viewStates[this.currentContentId] = this.getInitialViewStates(this.isStaticPage);

    this.protoOnInit();
  }

  public ngOnDestroy(): void {
    this.unsubscribe();

    this.protoOnDestroy();
  }

  public async refresh(id?: string, event?: CustomEvent): Promise<void> {
    // event is set when refresh happens via ion refresher
    if (event) {
      this.refresherElement = event.target as HTMLIonRefresherElement;
    }

    if (id) {
      this.viewStates[id].isRefreshing = !!event;
      this.viewStates[id].isLoading = true;
    }

    await this.reloadContent(id);

    return Promise.resolve();
  }

  // eslint-disable-next-line multiline-comment-style
  /** navigation helper function for external and internal pages
   *
   * external: `this.navigate('http://google.de').catch();`
   *
   * internal with routing parameter and navigation options (e.g. navigate from list to detail):
   * ```
   * const options = { relativeTo: this.activatedRoute, state; { item } }
   * await this.navigate([item.id], options);
   * ```
   *
   * internal with clearing history via using navigateRoot: await this.navigate('/main/more', null, 'root');
   */
  public navigate(url: string | any[] | UrlTree,
    options: NavigationOptions = {},
    direction: 'forward' | 'back' | 'root' = 'forward'): Promise<boolean> {
    if (typeof url === 'string' && url.startsWith('http')) {
      this.inAppBrowserService.openUrl(url);

      return Promise.resolve(true);
    }

    if (direction === 'forward') {
      return this.navController.navigateForward(url, options);
    }

    if (direction === 'back') {
      return this.navController.navigateBack(url, options);
    }

    return this.navController.navigateRoot(url, options);
  }

  // custom back button logic
  public async navigateBack(): Promise<boolean> {
    const canGoBack = await this.navController.pop();

    if (canGoBack) {
      return Promise.resolve(true);
    }

    if (this.currentTabUrl) {
      return this.navigate(this.currentTabUrl, {}, 'back');
    }

    return Promise.resolve(false);
  }

  // Developer-defined state that can be passed to any navigation
  // useful to deliver already loaded items (e.g. navigating from list to detail)
  protected getNavigationState(): { [key: string]: any } {
    const currentNavigation = this.router.getCurrentNavigation();

    if (!currentNavigation) {
      return {};
    }

    return currentNavigation.extras?.state;
  }

  protected ionViewDidEnter(): void {
    this.pageIsVisible = true;
    this.protoViewDidEnter();

    if (this.isStaticPage) {
      return;
    }

    // prevent double subscription
    if (this.ngUnsubscribe) {
      return;
    }

    this.ngUnsubscribe = new Subject<void>();

    const consoleMessage = '%c[BasePage] isStaticPage = false -> subscribeToContent() "%s"';

    console.info(consoleMessage, `color: ${DEBUG_COLORS.DEFAULT}`, this.constructor.name);
    this.subscribeToContent();
  }

  protected ionViewDidLeave(): void {
    this.pageIsVisible = false;
    console.info('%c[BasePage] protoViewDidLeave() "%s"', `color: ${DEBUG_COLORS.SECONDARY}`, this.constructor.name);
    this.protoViewDidLeave();
  }

  // wait until view did enter before subscribing
  protected protoViewDidEnter(): void {
    console.info('%c[BasePage] protoViewDidEnter() "%s"', `color: ${DEBUG_COLORS.DEFAULT}`, this.constructor.name);
  }

  protected subscribeToContent(): void {
    const consoleMessage = '%c[BasePage] no subscription has been made in page "%s"';

    console.info(consoleMessage, `color: ${DEBUG_COLORS.WARN}`, this.constructor.name);
    this.unsubscribe();
  }

  protected completeRefresher(): void {
    console.info('%c[BasePage] completeRefresher() "%s"', `color: ${DEBUG_COLORS.DEFAULT}`, this.constructor.name);
    if (this.refresherElement) {
      this.refresherElement.complete().catch();
    }
  }

  protected unsubscribe(): void {
    if (!this.ngUnsubscribe) {
      return;
    }

    console.info('%c[BasePage] unsubscribe() "%s"', `color: ${DEBUG_COLORS.SECONDARY}`, this.currentContentId);

    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  protected async presentModal<ModalData>(_options: ModalOptions): Promise<{ data: ModalData; role: string }> {
    const topModal = await this.modalController.getTop();
    const options: ModalOptions = _options;

    if (!options.component) {
      const consoleMessage = 'Failed to show modal. Please ensure you have provided a valid page component in';

      console.info(`%c[BasePage] presentModal() -> ${consoleMessage} "%s"`, `color: ${DEBUG_COLORS.ERROR}`, this.constructor.name);

      return Promise.resolve(null);
    }

    if (!options.presentingElement) {
      options.presentingElement = topModal || document.querySelector('ion-router-outlet');
    }

    const modal = await this.modalController.create(options);

    // `@Input() public presentingElement` in modal page
    options.componentProps.presentingElement = modal;

    await modal.present();

    const { data, role } = await modal.onWillDismiss();

    return Promise.resolve({
      data,
      role,
    });
  }

  protected setCurrenTabUrl(): void {
    const currentNavigation = this.router.getCurrentNavigation();

    if (!currentNavigation) {
      return;
    }

    // get active tab
    // expected url format '/main/<ACTIVE_TAB>/...'
    // only set default href when current page is not tab page
    try {
      const tree: UrlTree = currentNavigation.extractedUrl;
      const segmentGroup: UrlSegmentGroup = tree.root.children[PRIMARY_OUTLET];

      if (!segmentGroup?.segments) {
        this.isRootPage = true;

        return;
      }

      // don't set tab url when current url already is root page
      if (segmentGroup.segments.length > 2) {
        this.currentTabUrl = `/main/${segmentGroup.segments[1].path}`;
        console.info('%c[BasePage] setCurrenTabUrl(): "%s"', `color: ${DEBUG_COLORS.SECONDARY}`, this.currentTabUrl);
      }

      this.isRootPage = !this.currentTabUrl;
    } catch (error) {
      console.error(error.message);
    }
  }

  protected reloadContent(id: string): Promise<void> {
    const consoleMessage = '%c[BasePage] reloadContent() not implemented for page "%s"';

    console.info(consoleMessage, `color: ${DEBUG_COLORS.ERROR}`, this.constructor.name);

    this.onRequestFinished(id);

    return Promise.resolve();
  }

  protected onRequestFinished(id: string): void {
    this.viewStates[id].showSkeleton = false;
    this.viewStates[id].isLoading = false;

    if (this.viewStates[id].isRefreshing) {
      this.completeRefresher();
      this.viewStates[id].isRefreshing = false;
    }
  }

  protected isCanceledRequest(error?: Partial<HttpErrorResponse>): boolean {
    if (!error || error.status !== 0) {
      return false;
    }

    const eventType = (error.error as ProgressEvent)?.type;
    const message = `${error.message || ''} ${error.statusText || ''}`.toLowerCase();

    return eventType === 'abort' || (/abort|cancell?ed/u).test(message);
  }

  protected getBackButtonText(title?: string): string {
    if (this.uiPlatform.is('android')) {
      return '';
    }

    console.warn('TODO: show full back button text on ios when title is not too long', title);

    const { queryParams } = this.activatedRoute.snapshot;

    return queryParams.backButtonText || '';
  }

  protected getInitialViewStates(contentLoaded: boolean): ViewStates {
    return {
      hasError: false,
      hasMore: false,
      isEmpty: false,
      isLoading: !contentLoaded,
      isLoadingMore: false,
      isRefreshing: false,
      showSkeleton: !contentLoaded,
    };
  }

  protected protoOnInit(): void {
    console.info('%c[BasePage] protoOnInit() "%s"', `color: ${DEBUG_COLORS.SECONDARY}`, this.currentContentId);
  }

  protected protoOnDestroy(): void {
    console.info('%c[BasePage] protoOnDestroy() "%s"', `color: ${DEBUG_COLORS.SECONDARY}`, this.currentContentId);
  }

  // eslint-disable-next-line no-empty-function
  protected protoViewDidLeave(): void {}
}
